import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

// Add this interface at the top of the file
interface NewsItem {
  url: string;
  image: string;
  title: string;
  description: string;
  publishedAt: string;
}

// NewsList Component
const NewsList = ({ news }: { news: NewsItem[] }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {news.map((item: NewsItem) => (
      <div
        key={item.url}
        className="cursor-pointer bg-white shadow-lg hover:shadow-xl transition-shadow duration-300"
        onClick={() => window.open(item.url, '_blank')}
      >
        <img
          src={item.image || 'https://placehold.co/600x400'}
          alt={item.title}
          className="w-full h-48 object-cover"
        />
        <div className="p-4">
          <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
          <p className="text-sm text-gray-700 mb-4">{item.description}</p>
          <p className="text-sm text-gray-500">{item.publishedAt}</p>
        </div>
      </div>
    ))}
  </div>
);

const NewsUpdatesPage = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1); // Track the current page
  const [hasMore, setHasMore] = useState(true); // Whether there is more news to load

  // Function to fetch news about legal laws
  const fetchNews = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<{ articles: NewsItem[] }>(
        'https://gnews.io/api/v4/search',
        {
          params: {
            q: 'legal law',
            lang: 'es',
            max: 6,
            // TODO: change to env variable. Or not use it at all.
            token: '5de45e642e539d520472a1863750f66b',
          },
          // Add timeout to prevent hanging requests
          timeout: 5000,
        }
      );

      // Validate response data
      if (!response.data?.articles?.length) {
        setHasMore(false);
        return;
      }

      setNews((prevNews) => [...prevNews, ...response.data.articles]);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Please try again.');
        } else if (err.response?.status === 429) {
          setError('Too many requests. Please try again later.');
        } else if (!navigator.onLine) {
          setError('You appear to be offline. Please check your connection.');
        } else {
          setError('Failed to fetch news. Please try again later.');
        }
      } else {
        setError('An unexpected error occurred.');
      }
      console.error('News fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add abort controller for cleanup
  useEffect(() => {
    const controller = new AbortController();

    fetchNews();

    return () => {
      controller.abort();
    };
  }, [page]);

  // Load more news
  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-5xl font-bold mb-8">News & Updates</h1>

      {loading && !news.length && (
        <div className="flex justify-center">
          <p>Loading news...</p>
        </div>
      )}

      {error && <p className="text-red-600">{error}</p>}

      <h3 className="text-2xl font-bold mb-6">Latest Legal News</h3>
      <NewsList news={news} />

      {/* Load More Button with Spinner */}
      {hasMore && (
        <div className="flex justify-center mb-10">
          <button
            className="mt-6 px-6 py-2 mx-auto bg-gray-600 text-white font-bold rounded-md hover:bg-gray-700 flex items-center"
            onClick={handleLoadMore}
            disabled={loading} // Disable button when loading
          >
            {loading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin mr-2"
                />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}

      {loading && news.length > 0 && (
        <div className="flex justify-center">
          <p>Loading more news...</p>
        </div>
      )}
    </div>
  );
};

export default NewsUpdatesPage;
